/**
 * The garden plot — the ground the player puts down in their own world.
 *
 * A circular terrace ringed by a continuous walkway, with planting beds set
 * into the inner terrace and a marker stone at the edge. Round because a
 * round thing has no front: it is placed on an unknown floor and looked at
 * from wherever the player happens to be standing.
 *
 * ## It grows
 *
 * The plot is built for a *tier*. Tier 0 is the terrace a player places on
 * their floor; each tier after it adds a concentric district — Charbagh
 * Oasis, Sunken Eco-Wadi, Aquatic Wetland — and is unlocked by trees grown.
 * See `src/game/tiers.ts` for the rules, and `src/content/tiers.ts` for what
 * each district is meant to teach.
 *
 * Two things hold the whole design together:
 *
 * **Bed positions are absolute and permanent.** A ring is fixed in metres, so
 * expanding the plot extends the ground without moving a single planted
 * specimen. Growing the garden must never make the garden rearrange itself.
 *
 * **The districts are real terraces, stepped down.** Each outer platform is a
 * shorter cylinder than the one inside it, so the wadi genuinely sits below
 * grade and the wetland below that, which is how those gardens are really
 * built.
 * A ring's beds carry the y of their own platform, again fixed once opened.
 */

import * as THREE from 'three';
import { TIERS } from '../content/tiers';

export interface Plot {
  root: THREE.Group;
  /** Local-space positions where plants may be sown, innermost ring first. */
  slots: THREE.Vector3[];
  /** Radius of the outermost terrace, metres. */
  radius: number;
  /** Which tier this plot was built for. */
  tier: number;
  /**
   * Height of the walkable deck at a distance from the centre, metres.
   *
   * The districts are stepped down — the wadi genuinely sits below grade and
   * the wetland below that — so anything that walks across the plot has to
   * ask rather than assume. Beds already carry the y of their own platform;
   * this is the same answer for the ground between them.
   */
  groundYAt(radius: number): number;
  /** Plays the spawn animation. Call once after placing. */
  reveal(): void;
  update(dt: number, time: number): void;
  dispose(): void;
}

const SAND = 0xd9c9a8;
const STONE = 0xb9ad96;
const PATH = 0xe8e0d0;
const GRASS = 0x6f9455;
/*
 * District palette. The first pass was far too polite — a charbagh three
 * shades off the terrace and a wadi two off that, and from a normal viewing
 * distance the whole plot read as one flat disc of sand. Each district now
 * takes a decisively different value, because the *reason* to grow the garden
 * has to be visible from where the player actually sits.
 */
/** Charbagh paving: cut limestone, cool and much lighter than the terrace. */
const PAVING = 0xf0ead9;
const PAVING_EDGE = 0xc3b79b;
const WATER = 0x2f8fb0;
/** Wadi floor: sunk below grade and in shade nearly all day. */
const WADI_SAND = 0x8c7a5c;
const WADI_WALL = 0x8a7959;
const ROCK = 0x8f8068;
const REED = 0x4d7f4a;
const LOTUS = 0x3f8a52;
const PLANK = 0x9d7f5c;

/**
 * Height of each platform's top surface, metres.
 *
 * Constant per platform and never derived from the plot's current radius —
 * a bed's y is fixed the moment its ring opens, exactly like its x and z.
 */
const DECK = [0.063, 0.036, 0.016];

/** Which platform each tier's beds stand on. */
const TIER_DECK = [0, 0, 1, 2];

export function buildPlot(tier = 0): Plot {
  const t = THREE.MathUtils.clamp(tier, 0, TIERS.length - 1);
  const radius = TIERS[t].radius;

  const root = new THREE.Group();
  const owned: Array<{ dispose(): void }> = [];
  const track = <T extends { dispose(): void }>(x: T): T => {
    owned.push(x);
    return x;
  };

  const sandMat = track(
    new THREE.MeshStandardMaterial({ color: SAND, roughness: 1, flatShading: true }),
  );
  const stoneMat = track(
    new THREE.MeshStandardMaterial({ color: STONE, roughness: 0.9, flatShading: true }),
  );
  const pathMat = track(
    new THREE.MeshStandardMaterial({ color: PATH, roughness: 0.85 }),
  );
  const grassMat = track(
    new THREE.MeshStandardMaterial({ color: GRASS, roughness: 0.95, flatShading: true }),
  );

  /*
   * Platform stack. Each opened platform is a solid cylinder, shorter than
   * the one inside it, drawn outermost first. Nesting them this way is what
   * makes the section read as terraces cut into the ground rather than as
   * flat rings painted on one slab.
   */
  const PLATFORM_R = [t === 0 ? TIERS[0].radius : TIERS[1].radius, TIERS[2].radius, TIERS[3].radius];
  const riserMat = track(
    new THREE.MeshStandardMaterial({ color: WADI_WALL, roughness: 1, flatShading: true }),
  );
  const platforms = Math.max(1, t);
  for (let p = platforms - 1; p >= 0; p--) {
    const r = PLATFORM_R[p];
    const deck = DECK[p];
    const deckMesh = new THREE.Mesh(
      track(new THREE.CylinderGeometry(r, r * 0.97, deck, p === 0 ? 40 : 56)),
      p === 0 ? stoneMat : riserMat,
    );
    deckMesh.position.y = deck / 2;
    deckMesh.receiveShadow = true;
    deckMesh.castShadow = true;
    root.add(deckMesh);
  }

  const baseH = DECK[0];

  /** A flat annular surface laid on a platform. */
  const surface = (
    inner: number,
    outer: number,
    y: number,
    mat: THREE.Material,
    segments = 56,
  ) => {
    const geo =
      inner <= 0
        ? track(new THREE.CircleGeometry(outer, segments))
        : track(new THREE.RingGeometry(inner, outer, segments));
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = y;
    mesh.receiveShadow = true;
    root.add(mesh);
    return mesh;
  };

  /* ---- Tier 0 — the Palm Terrace ---------------------------------- */

  const T0 = TIERS[0].radius;
  surface(0, T0 * 0.78, baseH + 0.001, sandMat, 40);
  surface(T0 * 0.78, T0 * 0.95, baseH + 0.0012, pathMat, 48);

  // Grass tufts across the inner bed. Instanced — there are a lot of them and
  // they never move independently. Pinned to the tier-0 radius so the terrace
  // a player first places is unchanged by anything that opens later.
  const tuftGeo = track(new THREE.ConeGeometry(T0 * 0.018, T0 * 0.075, 4));
  const TUFTS = 64;
  const tufts = new THREE.InstancedMesh(tuftGeo, grassMat, TUFTS);
  tufts.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  const pos = new THREE.Vector3();
  for (let i = 0; i < TUFTS; i++) {
    // Sunflower distribution keeps them even without clumping.
    const u = (i + 0.5) / TUFTS;
    const r = Math.sqrt(u) * T0 * 0.72;
    const a = i * 2.399963;
    pos.set(Math.cos(a) * r, baseH + T0 * 0.035, Math.sin(a) * r);
    q.setFromEuler(new THREE.Euler(0, a, Math.sin(i) * 0.3));
    scl.setScalar(0.6 + ((i * 37) % 10) / 14);
    tufts.setMatrixAt(i, m.compose(pos, q, scl));
  }
  tufts.instanceMatrix.needsUpdate = true;
  tufts.castShadow = true;
  root.add(tufts);

  /* ---- Tier 1 — the Charbagh Oasis -------------------------------- */

  if (t >= 1) {
    const pavingMat = track(
      new THREE.MeshStandardMaterial({ color: PAVING, roughness: 0.8 }),
    );
    const waterMat = track(
      new THREE.MeshStandardMaterial({
        color: WATER,
        roughness: 0.18,
        metalness: 0.1,
        flatShading: true,
      }),
    );

    const edgeMat = track(
      new THREE.MeshStandardMaterial({ color: PAVING_EDGE, roughness: 0.9 }),
    );
    surface(T0 * 0.95, TIERS[1].radius * 0.97, baseH + 0.0011, pavingMat);
    // A darker band at the rim. Without it the paving runs straight into the
    // deck's own edge and the district has no outline at all.
    surface(TIERS[1].radius * 0.9, TIERS[1].radius * 0.97, baseH + 0.0014, edgeMat);

    /*
     * The four rills. This is the whole idea of a charbagh — a garden quartered
     * by water on the cardinal axes — so they run right through the paving to
     * a basin at the centre, and the eight new beds are phased to sit between
     * them rather than on them.
     */
    const rillGeo = track(new THREE.BoxGeometry(TIERS[1].radius * 1.78, 0.005, 0.062));
    const rills = new THREE.InstancedMesh(rillGeo, waterMat, 4);
    const o = new THREE.Object3D();
    for (let i = 0; i < 4; i++) {
      o.position.set(0, baseH + 0.0026, 0);
      // Two crossed bars make four arms — the fourfold division the whole
      // form is named for. Four separate bars would overlap at the centre.
      o.rotation.set(0, (i / 4) * Math.PI * 2, 0);
      o.scale.set(1, 1, 1);
      o.updateMatrix();
      rills.setMatrixAt(i, o.matrix);
    }
    rills.instanceMatrix.needsUpdate = true;
    rills.frustumCulled = false;
    root.add(rills);

    // Central basin, octagonal the way the real ones are.
    const basin = new THREE.Mesh(
      track(new THREE.CylinderGeometry(0.108, 0.118, 0.01, 8)),
      waterMat,
    );
    basin.position.y = baseH + 0.005;
    root.add(basin);
  }

  /* ---- Tier 2 — the Sunken Eco-Wadi ------------------------------- */

  if (t >= 2) {
    const wadiMat = track(
      new THREE.MeshStandardMaterial({ color: WADI_SAND, roughness: 1, flatShading: true }),
    );
    const rockMat = track(
      new THREE.MeshStandardMaterial({ color: ROCK, roughness: 1, flatShading: true }),
    );
    const reedMat = track(
      new THREE.MeshStandardMaterial({ color: REED, roughness: 0.95, flatShading: true }),
    );

    const y = DECK[1];
    surface(TIERS[1].radius, TIERS[2].radius * 0.97, y + 0.001, wadiMat);

    // Rock and reed scatter in the band between the wadi's inner lip and its
    // bed ring, so nothing lands where a plant will stand.
    const scatter = (
      geo: THREE.BufferGeometry,
      mat: THREE.Material,
      count: number,
      seed: number,
      lo: number,
      hi: number,
      scale: (i: number) => THREE.Vector3,
    ) => {
      const mesh = new THREE.InstancedMesh(geo, mat, count);
      const o = new THREE.Object3D();
      for (let i = 0; i < count; i++) {
        const a = ((i * 2.399963 + seed) % (Math.PI * 2)) + 0.0;
        const r = lo + (((i * 7 + seed * 13) % 100) / 100) * (hi - lo);
        o.position.set(Math.cos(a) * r, y + 0.001, Math.sin(a) * r);
        o.rotation.set(0, a * 1.7, 0);
        o.scale.copy(scale(i));
        o.updateMatrix();
        mesh.setMatrixAt(i, o.matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      root.add(mesh);
      return mesh;
    };

    scatter(
      track(new THREE.IcosahedronGeometry(0.045, 0)),
      rockMat,
      22,
      1,
      TIERS[1].radius + 0.03,
      TIERS[2].radius - 0.05,
      (i) => new THREE.Vector3(1, 0.6, 1).multiplyScalar(0.6 + ((i * 31) % 10) / 12),
    );
    scatter(
      track(new THREE.ConeGeometry(0.016, 0.135, 4)),
      reedMat,
      54,
      2,
      TIERS[1].radius + 0.02,
      TIERS[2].radius - 0.03,
      (i) => new THREE.Vector3(1, 0.7 + ((i * 17) % 10) / 10, 1),
    );
  }

  /* ---- Tier 3 — the Aquatic Wetland ------------------------------- */

  if (t >= 3) {
    const wetMat = track(
      new THREE.MeshStandardMaterial({
        color: WATER,
        roughness: 0.16,
        metalness: 0.12,
      }),
    );
    const lotusMat = track(
      new THREE.MeshStandardMaterial({ color: LOTUS, roughness: 0.85, flatShading: true }),
    );
    const plankMat = track(
      new THREE.MeshStandardMaterial({ color: PLANK, roughness: 0.95, flatShading: true }),
    );

    const y = DECK[2];
    surface(TIERS[2].radius, TIERS[3].radius * 0.97, y + 0.001, wetMat);

    // Floating lotus pads.
    const padGeo = track(new THREE.CircleGeometry(0.03, 7));
    const pads = new THREE.InstancedMesh(padGeo, lotusMat, 40);
    const o = new THREE.Object3D();
    for (let i = 0; i < 40; i++) {
      const a = i * 2.399963;
      const r = TIERS[2].radius + 0.03 + (((i * 11) % 100) / 100) * 0.25;
      o.position.set(Math.cos(a) * r, y + 0.0028, Math.sin(a) * r);
      o.rotation.set(-Math.PI / 2, 0, a);
      o.scale.setScalar(0.7 + ((i * 23) % 10) / 14);
      o.updateMatrix();
      pads.setMatrixAt(i, o.matrix);
    }
    pads.instanceMatrix.needsUpdate = true;
    pads.frustumCulled = false;
    root.add(pads);

    /*
     * Boardwalks on the cardinals. They are the only way the wetland reads as
     * something you could walk out onto rather than a moat, and they line up
     * with the pavilion's gaps so the eye runs straight from the gate to the
     * water.
     */
    const span = TIERS[3].radius - TIERS[2].radius + 0.06;
    const walkGeo = track(new THREE.BoxGeometry(span, 0.01, 0.075));
    const walks = new THREE.InstancedMesh(walkGeo, plankMat, 4);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const mid = (TIERS[2].radius + TIERS[3].radius) / 2;
      o.position.set(Math.cos(a) * mid, y + 0.008, Math.sin(a) * mid);
      o.rotation.set(0, -a, 0);
      o.scale.set(1, 1, 1);
      o.updateMatrix();
      walks.setMatrixAt(i, o.matrix);
    }
    walks.instanceMatrix.needsUpdate = true;
    walks.castShadow = true;
    walks.frustumCulled = false;
    root.add(walks);
  }

  /* ---- Shared furniture ------------------------------------------- */

  // Shadow catcher. The plot's own decks receive shadows, but plants at the
  // outer ring throw theirs past them — onto the player's real floor. A plane
  // of pure shadow lets those land, which is most of what sells the garden as
  // actually sitting in the room.
  const catcher = new THREE.Mesh(
    track(new THREE.PlaneGeometry(radius * 3.4, radius * 3.4)),
    track(new THREE.ShadowMaterial({ opacity: 0.26 })),
  );
  catcher.rotation.x = -Math.PI / 2;
  catcher.position.y = 0.0004;
  catcher.receiveShadow = true;
  root.add(catcher);

  // Marker stone at the plot edge, angled toward the viewer's start position.
  const markerDeck = DECK[Math.min(t === 0 ? 0 : t - 1, DECK.length - 1)];
  const marker = new THREE.Mesh(
    track(new THREE.BoxGeometry(0.154, 0.21, 0.025)),
    stoneMat,
  );
  marker.position.set(0, markerDeck + 0.105, radius * 0.86);
  marker.rotation.x = -0.12;
  marker.castShadow = true;
  root.add(marker);

  /* ---- Beds -------------------------------------------------------- */

  const slots: THREE.Vector3[] = [];
  for (let i = 0; i <= t; i++) {
    const y = DECK[Math.min(TIER_DECK[i], DECK.length - 1)];
    for (const ring of TIERS[i].rings) {
      for (let k = 0; k < ring.count; k++) {
        const a = (k / ring.count) * Math.PI * 2 + ring.phase;
        slots.push(
          new THREE.Vector3(Math.cos(a) * ring.radius, y, Math.sin(a) * ring.radius),
        );
      }
    }
  }

  // Spawn animation state.
  let revealT = 1;
  root.scale.setScalar(1);

  return {
    root,
    slots,
    radius,
    tier: t,
    groundYAt(r) {
      // Platforms nest: the tall inner one is drawn last and covers the
      // shorter ones, so the first platform that contains `r` is the surface
      // actually stood on.
      for (let p = 0; p < platforms; p++) {
        if (r <= PLATFORM_R[p]) return DECK[p];
      }
      return DECK[Math.min(platforms, DECK.length) - 1];
    },
    reveal() {
      revealT = 0;
    },
    update(dt, time) {
      if (revealT < 1) {
        revealT = Math.min(1, revealT + dt * 1.6);
        // Overshoot slightly then settle — the plot should feel like it lands.
        const e = 1 - Math.pow(1 - revealT, 3);
        const overshoot = Math.sin(revealT * Math.PI) * 0.08;
        root.scale.setScalar(e + overshoot);
      }
      // A whisper of movement in the grass so the plot is never fully static.
      tufts.rotation.y = Math.sin(time * 0.4) * 0.012;
    },
    dispose() {
      for (const o of owned) o.dispose();
    },
  };
}
